import { useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'
import LazyLoad from 'react-lazyload'
import { downloadFabricStatus, formatDate } from '@trinity/utils'
import {
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  FabricCardList,
  FormControl,
  GetApp,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  UnfoldMore,
} from '../../components'
import { useGlobalState, type SxProps } from '../../hooks'

interface FabricExplorerCollectionProps {
  collection: TrinityAPI.CollectionExtendedType
  children?: React.ReactNode
}

export function FabricExplorerCollection({ collection, children = null }: FabricExplorerCollectionProps) {
  return (
    <Stack spacing={4}>
      <Stack direction='row' justifyContent='space-between'>
        {children}
        <Breadcrumbs
          aria-label='breadcrumb'
          sx={{ display: { mobile: 'none', laptop: 'initial' }, alignSelf: 'flex-end' }}
        >
          <Typography component={Link} variant='smallLink' to='/fabric-explorer/collections'>
            Collections
          </Typography>
          <Typography variant='smallBody1'>{collection.name}</Typography>
        </Breadcrumbs>
      </Stack>
      <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={4} justifyContent='space-between'>
        <Stack
          direction={{ mobile: 'column', laptop: 'row' }}
          spacing={2}
          alignItems={{ mobile: 'center', laptop: 'flex-start' }}
        >
          <img src={collection.image} alt={collection.title} height={100} />
          <div>
            <Typography gutterBottom variant='h4'>
              {collection.title}
            </Typography>
            <Typography variant='body1' sx={{ textAlign: { mobile: 'center', laptop: 'left' } }}>
              {collection.datecode}
            </Typography>
          </div>
        </Stack>
      </Stack>
      <Outlet />
    </Stack>
  )
}

function CollectionButtonWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      direction={{ mobile: 'column', laptop: 'row' }}
      spacing={1}
      alignItems='center'
      justifyContent={{ mobile: 'center', laptop: 'flex-end' }}
      mt={{ laptop: '-4.6rem !important' }}
    >
      {children}
    </Stack>
  )
}

const FABRIC_STOCK_FILTER_ALL = 'all'
const FABRIC_STOCK_FILTER_IN_STOCK = 'inStock'
const FABRIC_STOCK_FILTER_TEMP_OUT = 'tempOut'
const FABRIC_STOCK_FILTER_UNAVAILABLE = 'unavailable'
type FabricStockFilter =
  | typeof FABRIC_STOCK_FILTER_ALL
  | typeof FABRIC_STOCK_FILTER_IN_STOCK
  | typeof FABRIC_STOCK_FILTER_TEMP_OUT
  | typeof FABRIC_STOCK_FILTER_UNAVAILABLE

export function FabricExplorerCollectionDefault({ collection }: { collection: TrinityAPI.CollectionExtendedType }) {
  const { onMobile } = useGlobalState()
  const navigate = useNavigate()
  const [stockFilter, setStockFilter] = useState<FabricStockFilter>(FABRIC_STOCK_FILTER_ALL)

  const displayedFabrics = FilterDisplayedFabrics(collection.fabrics, stockFilter)

  return (
    <Stack spacing={4}>
      <CollectionButtonWrapper>
        <Button variant='outlined' onClick={() => navigate('status')}>
          View Fabric Status
        </Button>
        {collection.digitalPdfUrl && (
          <Button
            download
            variant='outlined'
            startIcon={<GetApp fontSize='large' />}
            href={collection.digitalPdfUrl}
            target='_blank'
            sx={{ display: { mobile: 'none', laptop: 'inherit' } }}
          >
            Digital Collection
          </Button>
        )}
      </CollectionButtonWrapper>
      <Divider />
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: 1 }}>
        <FilterFabricsControl stockFilter={stockFilter} setStockFilter={setStockFilter} />
        <Typography>
          {!onMobile && 'Showing '}
          {stockFilter !== FABRIC_STOCK_FILTER_ALL && (
            <Typography component='span'>{displayedFabrics.length} of </Typography>
          )}
          {collection.fabrics.length}
          {!onMobile && ' fabrics'}
        </Typography>
      </Stack>
      <FabricCardList fabrics={displayedFabrics} baseUrl='/fabric-explorer/fabrics/:fabricId' />
    </Stack>
  )
}

export function FabricExplorerCollectionStatus({ collection }: FabricExplorerCollectionProps) {
  const { onMobile } = useGlobalState()
  const { user } = useGlobalState()
  const navigate = useNavigate()
  const headers = onMobile ? mobileHeaders : desktopHeaders

  return (
    <Stack spacing={4}>
      <CollectionButtonWrapper>
        <Button onClick={() => navigate('..', { relative: 'path' })}>View Fabric Status</Button>
        <Button
          download
          variant='outlined'
          startIcon={<GetApp fontSize='large' />}
          href={downloadFabricStatus(collection.id, user?.dealer?.id)}
          sx={{ display: { mobile: 'none', laptop: 'inherit' } }}
        >
          Download PDF
        </Button>
      </CollectionButtonWrapper>
      <Stack>
        <Stack direction='row' alignItems='center' sx={{ ...sharedStyles.rowStyles, height: 38, bgcolor: 'grey.100' }}>
          {headers.map(headerTitle => (
            <Typography
              key={headerTitle}
              variant='htable'
              sx={{ flex: sharedStyles.columnFlexValues[headerTitle as HeaderTitles], textTransform: 'uppercase' }}
            >
              {headerTitle}
            </Typography>
          ))}
        </Stack>
        {collection.fabrics.map(fabric => (
          <LazyLoad key={fabric.id} placeholder={<SkeletonStatusRow />}>
            <Stack direction='row' sx={{ ...sharedStyles.rowStyles, py: 2, borderBottom: 'n100' }}>
              <Fabric fabric={fabric} />
              {!onMobile && <Description fabric={fabric} />}
              {!onMobile && <Readiness fabric={fabric} />}
              <Status fabric={fabric} />
              {!onMobile && <Restock fabric={fabric} />}
              {!onMobile && <LastUpdate fabric={fabric} />}
            </Stack>
          </LazyLoad>
        ))}
      </Stack>
    </Stack>
  )
}

const sharedStyles: SharedStyleType = {
  rowStyles: { px: { mobile: 2, laptop: 4 } },
  cellSpacing: 2.5,
  columnFlexValues: {
    fabric: 1,
    description: 2,
    readiness: 1,
    status: 1,
    restock: 1,
    'last update': 1,
  },
}
const mobileHeaders = ['fabric', 'status']
const desktopHeaders = Object.keys(sharedStyles.columnFlexValues)

//* TYPES
interface ColumnFlexValuesType {
  fabric: number
  description: number
  readiness: number
  status: number
  restock: number
  'last update': number
}

interface SharedStyleType {
  rowStyles: SxProps
  cellSpacing: React.ComponentProps<typeof Stack>['spacing']
  columnFlexValues: ColumnFlexValuesType
}

type HeaderTitles = keyof ColumnFlexValuesType

interface FabricProps {
  fabric: TrinityAPI.FabricExtendedType
}

//* HELPER COMPONENTS
const SkeletonHelper = ({ width }: { width: number }) => (
  <Stack spacing={2} sx={{ flex: 1 }}>
    <Typography variant='smallBody1'>
      <Skeleton sx={{ width }} />
    </Typography>
  </Stack>
)

function SkeletonStatusRow() {
  const { onMobile } = useGlobalState()

  return (
    <Stack direction='row' sx={{ ...sharedStyles.rowStyles, py: 2, borderBottom: 'n100' }}>
      {/* //* FABRIC */}
      <Typography variant='smallBody1' sx={{ flex: 1 }}>
        <Skeleton sx={{ width: 3 / 5 }} />
      </Typography>
      {/* //* DESCRIPTION */}
      {!onMobile && (
        <Typography variant='smallBody1' sx={{ flex: 2 }}>
          <Skeleton sx={{ width: 4 / 5 }} />
        </Typography>
      )}
      {/* //* READINESS */}
      {!onMobile && <SkeletonHelper width={2 / 3} />}
      {/* //* STATUS */}
      <SkeletonHelper width={1 / 3} />
      {/* //* RESTOCK */}
      {!onMobile && <SkeletonHelper width={2 / 3} />}
      {/* //* LAST UPDATE */}
      {!onMobile && <SkeletonHelper width={2 / 3} />}
    </Stack>
  )
}

const Fabric = ({ fabric }: FabricProps) => {
  const { onMobile } = useGlobalState()

  return (
    <Stack
      justifyContent='center'
      spacing={sharedStyles.cellSpacing}
      sx={{ flex: sharedStyles.columnFlexValues.fabric }}
    >
      <Typography component={Link} to={`/fabric-explorer/fabrics/${fabric.id}`} variant='smallLink'>
        {fabric.trinityFabricNumber}
      </Typography>
      {onMobile && (
        <Typography variant='smallBody1' sx={{ maxHeight: 35, overflow: 'hidden' }}>
          {fabric.description}
        </Typography>
      )}
    </Stack>
  )
}

const Description = ({ fabric }: FabricProps) => (
  <Typography variant='smallBody1' sx={{ flex: sharedStyles.columnFlexValues.description, alignSelf: 'center' }}>
    {fabric.description}
  </Typography>
)

const Readiness = ({ fabric }: FabricProps) => (
  <Stack
    justifyContent='center'
    spacing={sharedStyles.cellSpacing}
    sx={{ flex: sharedStyles.columnFlexValues.readiness }}
  >
    <Typography variant='smallBody1' sx={{ textTransform: 'capitalize' }}>
      {fabric.availability?.readiness}
    </Typography>
  </Stack>
)

const Status = ({ fabric }: FabricProps) => (
  <Stack
    justifyContent='center'
    spacing={(sharedStyles.cellSpacing as number) - 0.5}
    sx={{ flex: sharedStyles.columnFlexValues.status }}
  >
    {fabric.availability ? (
      <Chip
        size='small'
        label={<Typography variant='label'>{fabric.availability.inStock ? 'In Stock' : 'Temp Out'}</Typography>}
        color={fabric.availability.inStock ? 'success' : 'warning'}
        sx={{ width: 'fit-content' }}
      />
    ) : (
      <Chip
        size='small'
        label={<Typography variant='label'>Not Available</Typography>}
        color='error'
        sx={{ width: 'fit-content' }}
      />
    )}
  </Stack>
)

const Restock = ({ fabric }: FabricProps) => (
  <Stack
    justifyContent='center'
    spacing={sharedStyles.cellSpacing}
    sx={{ flex: sharedStyles.columnFlexValues.restock }}
  >
    <Typography variant='smallBody1'>
      {fabric.availability?.restockDate ? formatDate(fabric.availability.restockDate) : '--'}
    </Typography>
  </Stack>
)

const LastUpdate = ({ fabric }: FabricProps) => (
  <Stack
    justifyContent='center'
    spacing={sharedStyles.cellSpacing}
    sx={{ flex: sharedStyles.columnFlexValues['last update'] }}
  >
    <Typography variant='smallBody1'>
      {fabric.availability?.lastEditDate ? formatDate(fabric.availability.lastEditDate) : '--'}
    </Typography>
  </Stack>
)

const FilterDisplayedFabrics = (fabrics: TrinityAPI.FabricExtendedType[], stockFilter: FabricStockFilter) => {
  switch (stockFilter) {
    case FABRIC_STOCK_FILTER_IN_STOCK:
      return fabrics.filter(f => Boolean(f.availability?.inStock))
    case FABRIC_STOCK_FILTER_TEMP_OUT:
      return fabrics.filter(f => {
        if (f.availability) return !f.availability.inStock
        return false
      })
    case FABRIC_STOCK_FILTER_UNAVAILABLE:
      return fabrics.filter(f => !f.availability)
    default:
      return fabrics
  }
}

const FilterFabricsControl = ({
  stockFilter,
  setStockFilter,
}: {
  stockFilter: FabricStockFilter
  setStockFilter: React.Dispatch<React.SetStateAction<FabricStockFilter>>
}) => (
  <FormControl component='fieldset'>
    <TextField
      select
      size='small'
      label='Availability'
      slotProps={{ select: { IconComponent: UnfoldMore } }}
      sx={{ width: { mobile: 200, tablet: 300 } }}
      onChange={e => setStockFilter(e.target.value as FabricStockFilter)}
      defaultValue={stockFilter}
    >
      <MenuItem value={FABRIC_STOCK_FILTER_ALL}>All</MenuItem>
      <MenuItem value={FABRIC_STOCK_FILTER_IN_STOCK}>In Stock</MenuItem>
      <MenuItem value={FABRIC_STOCK_FILTER_TEMP_OUT}>Temp. Out</MenuItem>
      <MenuItem value={FABRIC_STOCK_FILTER_UNAVAILABLE}>Not Available</MenuItem>
    </TextField>
  </FormControl>
)
